import { Logger } from '../Logger';
import { AotCategory, logConstants } from '../../utils/Constants';
import {
	HTTPFetchResponse,
	HeadersJson,
	PrimaryTokenStatus,
	QueuedRequest,
} from '../dsAuth/data/AuthTypes';
import GatewaySession from './GatewaySession';
import { gatewayConstants } from '../AuthConstants';
import { CookieManager } from '../CookieManager';
import AuthMan from '../AuthMan';
import { v4 as uuidv4 } from 'uuid';
import { stringConstants } from '../AuthConstants';
import { GatewayAuthError, GatewaySessionError } from '../AuthErrors';
import { LogonResult } from './Definitions';
import { UrlTranslationType } from '../main';
import { StoreType } from '../../Common/TypeDefinitions';

export type ProcessQueueReason = 'success' | 'cancelled';

/**
 * @class main entry point for gateway authentication
 * @member instance - singleton instance, used to access the class
 * @member currentSessions - list of all current gateway sessions and their data
 * @member defaultGateways - map of store URLs and its default gateways
 */
export default class GatewayService {
	private static instance: GatewayService;
	private currentSessions: GatewaySession[] = [];
	private defaultGateways: Map<string, string> = new Map<string, string>();

	static getInstance(): GatewayService {
		if (!GatewayService.instance) {
			GatewayService.instance = new GatewayService();
			Logger.getInstance().write(logConstants.INFO, 'Creating gateway service instance');
		}
		return GatewayService.instance;
	}

	/**
	 * API to make an authenticated HTTP request via gateway, this will check if there is a cached gateway session
	 * else creates a new session and makes the request
	 * @param method - method of the HTTP request
	 * @param url - URL of the request
	 * @param headers - HTTP Headers of the request in JSON format
	 * @param body - body of the request
	 * @param referenceUri - referenceUri, against which the cookies will be stored, should be same for subsequent requests
	 * @param id - transaction ID for the request
	 * @returns Promise<HTTPFetchResponse>
	 */
	public async fetch(
		method: string,
		url: string,
		headers: HeadersJson,
		body: string,
		referenceUri: string,
		allowCredUi: boolean,
		id?: string,
		gatewayUrl?: string
	): Promise<HTTPFetchResponse> {
		try {
			if (!gatewayUrl) {
				const storeConfig = AuthMan.getInstance().getStoreConfiguration();
				gatewayUrl = storeConfig[referenceUri]?.defaultGatewayLocation;
				Logger.getInstance().write(
					logConstants.INFO,
					`gatewayUrl not present in params - trying to get from storeConfigurations: ${gatewayUrl}`
				);
			}

			const serverUrl = new URL(url);
			const serverUrlOrigin = new URL(serverUrl.origin);

			const gateway = gatewayUrl ? new URL(gatewayUrl) : new URL(referenceUri);
			const activeSession = this.retrieveCachedGatewayIfPresent(gateway);

			let response;
			if (activeSession) {
				response = await activeSession.authenticatedFetch(
					method,
					url,
					headers,
					body,
					this.defaultGateways.get(serverUrl.origin) ?? referenceUri,
					allowCredUi,
					id
				);
			} else {
				if (!allowCredUi) {
					/**
					 * This is required in RTU scenarios where multiple accounts are added, and one of the on-prem accounts is used
					 * If -periodicpoll is called for the other on-prem store that was not initialised, it will try to create a session even with allowCredUi false
					 */
					Logger.getInstance().write(
						logConstants.INFO,
						`No active sessions present for the given gateway, aborting the request since allowCredUi is false`
					);
					throw new GatewaySessionError(LogonResult.GatewaySessionExpired);
				}

				Logger.getInstance().write(
					logConstants.INFO,
					`No active sessions present for the given gateway, creating a new session`,
					AotCategory.Info,
					`No active sessions present for the given gateway, creating a new session`
				);

				/**
				 * Using the existing AuthMan.requestQueue to handle multiple concurrent requests
				 *
				 * 1. Both go.citrix.com/Citrix/Roaming/accounts and go.citrix.com/Citrix/Store/discovery arrive at the same time
				 * 		The Roaming/accounts request is added to the queue and authentication is started for it and
				 * 		Store/discovery request is added to the queue and waits for the result of the first request.
				 * 			a. If the first request's authentication is successful, then the second request is resolved with AAAC_FETCHED status - can directly make a request
				 * 			b. If the first request's authentication is cancelled, then the second request is resolved with USER_CANCELLED_AUTH status - removed from the queue without trying to make a request
				 * 2. Different stores, ftlgo.citrix.com/Citrix/Roaming/accounts and pacgo.citrix.com/Citrix/Roaming/accounts
				 * 		One of the two sessions start authentication while the other request waits for the promise resolution in the queue. Since both these requests are
				 * 		from different FQDN's, the authentication result of one does not matter to the other. Hence, the second request is resolved with RESUME_PRIMARY_TOKEN_OR_AAAC_TOKEN status
				 *		meaning it should start authentication to fetch the AAAC cookie
				 */

				const requestId = uuidv4();
				const queuedRequest = new QueuedRequest(requestId, referenceUri);
				AuthMan.getInstance().requestQueue.push(queuedRequest);

				if (AuthMan.getInstance().isAuthInProgress) {
					Logger.getInstance().write(
						logConstants.INFO,
						`Auth is already in progress, waiting for it to complete`
					);

					const waitStatusObj: PrimaryTokenStatus = await new Promise(
						(resolve: Function, reject: Function) => {
							queuedRequest.waitStatusResolve = resolve;
							queuedRequest.reject = reject;
						}
					);

					if (waitStatusObj.status === stringConstants.USER_CANCELLED_AUTH) {
						throw new GatewayAuthError(
							'Aborting this request since earlier auth request was cancelled by the user'
						);
					} else if (waitStatusObj.status === stringConstants.NSC_AAAC_COOKIE_FETCHED) {
						const activeSession = this.retrieveCachedGatewayIfPresent(gateway);
						if (activeSession) {
							return await activeSession.authenticatedFetch(
								method,
								url,
								headers,
								body,
								this.defaultGateways.get(serverUrl.origin) ?? referenceUri,
								allowCredUi,
								id
							);
						}
					} else if (
						waitStatusObj.status !== stringConstants.RESUME_PRIMARY_TOKEN_OR_AAAC_FETCH
					) {
						throw new GatewayAuthError('Invalid resolve response recieved from Promise');
					}
				}

				AuthMan.getInstance().isAuthInProgress = true;

				try {
					const sessionUrl = gatewayUrl ? new URL(gateway.origin) : serverUrlOrigin;
					Logger.getInstance().writeAot(
						AotCategory.Info,
						`Creating a gateway session for ${sessionUrl}`
					);
					const gatewaySession = await this.createGatewaySession(sessionUrl);
					this.defaultGateways.set(serverUrl.origin, sessionUrl.origin);

					response = await gatewaySession.authenticatedFetch(
						method,
						url,
						headers,
						body,
						this.defaultGateways.get(serverUrl.origin) ?? referenceUri,
						allowCredUi,
						id
					);
					gatewaySession.gatewayUrl = new URL(this.defaultGateways.get(serverUrl.origin));

					if (!gatewaySession.getReferenceUri() && referenceUri) {
						gatewaySession.setReferenceUri(referenceUri);
					}

					Logger.getInstance().write(
						logConstants.INFO,
						`Created a gateway session successfully, pushing it to the list`,
						AotCategory.Info,
						`Created a gateway session successfully for ${sessionUrl}`
					);
					this.currentSessions.push(gatewaySession);

					AuthMan.getInstance().isAuthInProgress = false;
					this.processNextItemInQueue(referenceUri, 'success');
				} catch (e) {
					Logger.getInstance().write(
						logConstants.ERROR,
						'Error creating a new gateway session: ' + e.message,
						AotCategory.Error,
						`Error creating a new gateway session for ${serverUrlOrigin}`
					);
					AuthMan.getInstance().isAuthInProgress = false;
					this.processNextItemInQueue(referenceUri, 'cancelled');
					throw e;
				}
			}

			return response;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error during authenticated fetch: ' + e.message
			);
			AuthMan.getInstance().isAuthInProgress = false;
			throw e;
		}
	}

	/**
	 * API to fetch roaming accounts response via the gateway, given the store URL
	 * @param url - store URL for which the roaming accounts response is to be fetched
	 * @returns Promise<HTTPFetchResponse> - returns a promise of RoamingAccountsResponse
	 */
	public async getRoamingAccountsResponse(url: string): Promise<HTTPFetchResponse> {
		try {
			return await this.fetch(
				'GET',
				url + gatewayConstants.ROAMING_ACCOUNTS_PATH,
				{
					Accept: '*/*',
				},
				'',
				url,
				true,
				'',
				url
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Failed to get roaming accounts response for ${url}`
			);
			throw e;
		}
	}

	private processNextItemInQueue(referenceUri: string, reason: ProcessQueueReason): void {
		const filteredRequests = AuthMan.getInstance().requestQueue.filter(
			(req) => req.referenceUri === referenceUri
		);

		filteredRequests.forEach((req) => {
			if (req.waitStatusResolve) {
				Logger.getInstance().write(
					logConstants.INFO,
					`calling waitStatusResolve with ${reason} for ${req.id}:`
				);
				if (reason == 'success') {
					req.waitStatusResolve(
						new PrimaryTokenStatus(stringConstants.NSC_AAAC_COOKIE_FETCHED, true)
					);
				} else {
					req.waitStatusResolve(
						new PrimaryTokenStatus(stringConstants.USER_CANCELLED_AUTH, true)
					);
				}
			}
		});

		AuthMan.getInstance().requestQueue = AuthMan.getInstance().requestQueue.filter(
			(request) => request.referenceUri !== referenceUri
		);

		if (AuthMan.getInstance().requestQueue.length > 0) {
			const status = new PrimaryTokenStatus(
				stringConstants.RESUME_PRIMARY_TOKEN_OR_AAAC_FETCH,
				true
			);
			AuthMan.getInstance().requestQueue[0].waitStatusResolve(status);
		}
	}

	/**
	 * Function to check and return if there is an active gateway session
	 * @param requestUrl - request URL
	 * @returns GatewaySession
	 */
	private retrieveCachedGatewayIfPresent(requestUrl: URL): GatewaySession {
		for (const session of this.currentSessions) {
			if (
				session.getGatewayUrl().origin === requestUrl.origin ||
				session.getDirectUrl()?.origin === requestUrl.origin
			) {
				return session;
			}
		}
		return;
	}

	public getGatewaySessionType(requestUrl: string): StoreType {
		const session = this.retrieveCachedGatewayIfPresent(new URL(requestUrl));
		return session ? session.getServerType() : StoreType.Unknown;
	}

	/**
	 * Function to set the referenceUri to a gateway session given its sessionUrl
	 * @param sessionUrl - FQDN of the active gateway session
	 * @param referenceUri - referenceUri for the given gateway session
	 */
	public setReferenceUriToGatewaySession(sessionUrl: URL, referenceUri: string): boolean {
		for (const session of this.currentSessions) {
			if (
				session.getGatewayUrl().origin === sessionUrl.origin ||
				session.getDirectUrl()?.origin === sessionUrl.origin
			) {
				session.setReferenceUri(referenceUri);
				return true;
			}
		}
		return false;
	}

	/**
	 * Function to create a new gateway session object given the URL
	 * @param serverUrl - request URL
	 * @returns Promise<GatewaySession>
	 */
	private async createGatewaySession(serverUrl: URL): Promise<GatewaySession> {
		const gatewaySession = await GatewaySession.probeForForms(serverUrl);
		return gatewaySession;
	}

	/**
	 * Function to remove gateway session from the active sessions given the referenceUri
	 * @param referenceUri - Reference URI of the session to be removed
	 */
	public async removeGatewaySession(referenceUri: string): Promise<boolean> {
		try {
			Logger.getInstance().write(
				logConstants.INFO,
				`Removing gateway session: ${referenceUri}`
			);

			let isDeleted = false;
			const index = this.currentSessions.findIndex(
				(session) => session.getReferenceUri() == referenceUri
			);

			if (index != -1) {
				await this.currentSessions[index].delete();
				await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
					this.currentSessions[index].getGatewayUrl().origin
				);
				await CookieManager.getInstance().deleteSessionCookiesForReferenceUri(
					referenceUri
				);
				this.currentSessions.splice(index, 1);
				isDeleted = true;

				Logger.getInstance().write(
					logConstants.INFO,
					`Successfully deleted session cookies for ${referenceUri} and removed session from the list`
				);
			}

			return isDeleted;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error during removal of gateway session - ${referenceUri}`
			);
		}
	}

	/**
	 * Function to check if a given referenceUri has a logged in gateway session associated with it
	 * @param referenceUri
	 * @returns boolean
	 */
	public isSessionActive(referenceUri: string): boolean {
		const isActive = false;
		const index = this.currentSessions.findIndex(
			(session) => session.getReferenceUri() == referenceUri
		);

		if (index != -1) {
			return this.currentSessions[index].isSessionActive();
		}

		return isActive;
	}

	/**
	 * Function to signout of all active gateway sessions
	 * ! TODO - Modify to sign-out of only a given account
	 */
	public async signoutOfActiveSessions(): Promise<void> {
		Logger.getInstance().write(
			logConstants.INFO,
			`Signing out of all active gateway sessions`
		);
		for (const session of this.currentSessions) {
			await session.delete();
		}
	}

	/**
	 * Function to get the session cookie (NSC_AAAC cookie) for a gateway session if present, given its gateway URL
	 * @param gatewayUrl
	 * @returns
	 */
	public getSessionCookie(gatewayUrl: string): string {
		const session = this.retrieveCachedGatewayIfPresent(new URL(gatewayUrl));
		return session ? session.getSessionCookie() : '';
	}

	/**
	 * Function to get the translated URL for a given gateway URL if gateway session exists
	 * @param gatewayUrl
	 * @returns
	 */
	public getTranslatedUrl(gatewayUrl: string): string {
		const session = this.retrieveCachedGatewayIfPresent(new URL(gatewayUrl));
		return session
			? session.getTranslatedUrl(gatewayUrl, UrlTranslationType.StorefrontToGateway)
			: '';
	}

	/**
	 * Function to get the translated URL for a given URL if gateway session exists
	 * @param url
	 * @param referenceUri
	 * @param translationType
	 * @returns Promise<string>
	 */
	public async translateUrl(
		url: string,
		referenceUri: string,
		translationType: UrlTranslationType
	): Promise<string> {
		const session = this.retrieveCachedGatewayIfPresent(new URL(referenceUri));
		return session ? session.getTranslatedUrl(url, translationType) : url;
	}
}
